import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Input from "../ui/input";
import Select from "../ui/select";
import Textarea from "../ui/textarea";
import Button from "../ui/button";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    budget: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#2C2C2C] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 border border-white rounded-full" />
        <div className="absolute bottom-0 left-0 w-64 h-64 border border-white rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-16 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[1px] bg-[#C9A962]" />
              <span className="text-[#C9A962] tracking-[0.3em] text-xs uppercase">Get In Touch</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">
              Let's Create Something Beautiful Together
            </h2>
            <p className="text-white/60 text-lg mb-10">
              Ready to transform your space? Schedule a free consultation and let our experts guide you through the journey.
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#C9A962]" />
                </div>
                <div>
                  <div className="text-white/50 text-sm mb-1">Call Us</div>
                  <a href="tel:+918796063801" className="text-white hover:text-[#C9A962] transition-colors">
                    +91 87960 63801 / +91 87960 14195
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#C9A962]" />
                </div>
                <div>
                  <div className="text-white/50 text-sm mb-1">Email Us</div>
                  <a href="mailto:vedacreations.in" className="text-white hover:text-[#C9A962] transition-colors">
                    jharna@vedacreations.in
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#C9A962]" />
                </div>
                <div>
                  <div className="text-white/50 text-sm mb-1">Visit Us</div>
                  <p className="text-white">
                    Nimbus Park View2 Society,<br />
                    Flat No 802L1,<br/>
                    Tower Shiv, Greater Noida,<br/>
                    Uttar Pradesh, India
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#C9A962]" />
                </div>
                <div>
                  <div className="text-white/50 text-sm mb-1">Working Hours</div>
                  <p className="text-white">
                    Mon - Sat: 10:00 AM - 7:00 PM<br />
                    Sunday: By Appointment
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Contact Buttons */}
            <div className="flex gap-4">
              <a
                href="https://wa.me/918796063801"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 hover:bg-[#1fb855] transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
              <a
                href="tel:+918796063801"
                className="flex items-center gap-2 bg-white/10 text-white px-6 py-3 hover:bg-white/20 transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10">
              <h3 className="font-serif text-2xl text-[#2C2C2C] mb-6">
                Request Free Consultation
              </h3>

              <div className="space-y-5">
                <div>
                  <Input
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="rounded-none border-[#E8E2D9] focus:border-[#C9A962] h-12"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <Input
                    placeholder="Phone Number *"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="rounded-none border-[#E8E2D9] focus:border-[#C9A962] h-12"
                    required
                  />
                  <Input
                    placeholder="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-none border-[#E8E2D9] focus:border-[#C9A962] h-12"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  {/* <Select onValueChange={(value) => setFormData({ ...formData, projectType: value })}>
                    <SelectTrigger className="rounded-none border-[#E8E2D9] focus:border-[#C9A962] h-12">
                      <SelectValue placeholder="Project Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="office">Office</SelectItem>
                      <SelectItem value="hospitality">Hospitality</SelectItem>
                    </SelectContent>
                  </Select> */}
                  <Select
                    value={formData.projectType}
                    onChange={(value) =>
                        setFormData({ ...formData, projectType: value })
                    }
                    className="rounded-none border-[#E8E2D9] h-12"
                    options={[
                        { label: "Residential", value: "residential" },
                        { label: "Commercial", value: "commercial" },
                        { label: "Office", value: "office" },
                        { label: "Hospitality", value: "hospitality" },
                    ]}
                    />


                  {/* <Select onValueChange={(value) => setFormData({ ...formData, budget: value })}>
                    <SelectTrigger className="rounded-none border-[#E8E2D9] focus:border-[#C9A962] h-12">
                      <SelectValue placeholder="Budget Segment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                    </SelectContent>
                  </Select> */}
                  <Select
                        value={formData.budget}
                        onChange={(value) =>
                            setFormData({ ...formData, budget: value })
                        }
                        className="rounded-none border-[#E8E2D9] h-12"
                        options={[
                            { label: "Basic", value: "basic" },
                            { label: "Premium", value: "premium" },
                            { label: "Luxury", value: "luxury" },
                        ]}
                        />

                </div>

                <Textarea
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="rounded-none border-[#E8E2D9] focus:border-[#C9A962] min-h-[120px] resize-none"
                />

                <Button
                  type="submit"
                  className="w-full bg-[#C9A962] hover:bg-[#b8954f] text-white rounded-none py-6 text-base"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Request
                </Button>

                <p className="text-[#2C2C2C]/50 text-xs text-center">
                  By submitting, you agree to receive communications from Veda Creation & Group.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}